import React, { useEffect } from "react";
import "./About.css";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import { Helmet } from "react-helmet-async";

// Import your images
import heroImage from "../../assets/images/about-hero-1.jpg";
import valuesImage from "../../assets/images/about-team.png";
import teamImage from "../../assets/images/about-team.png";
import missionImage from "../../assets/images/mission.png";

const About = () => {
  useScrollAnimation();

  useEffect(() => {
    // Add animation classes to elements on load
    const animateElements = document.querySelectorAll(".animate-on-load");
    animateElements.forEach((element, index) => {
      element.style.animationDelay = `${0.2 + index * 0.1}s`;
    });
  }, []);

  return (
    <>
      <Helmet>
        <title>
          About Us | Avvoka - Exporters of Fresh Produce, Tea & Coffee
        </title>
        <meta
          name="description"
          content="Learn more about Avvoka, a trusted exporter of fresh produce, tea, and coffee from Kenya. Discover our history, mission, vision, and commitment to sustainability."
        />
        <meta
          name="keywords"
          content="About Avvoka, Kenyan exporters, fresh produce exports, tea exporters, coffee exporters, sustainable trade, export company Kenya"
        />
        <meta
          property="og:title"
          content="About Avvoka | Exporters of Fresh Produce, Tea & Coffee"
        />
        <meta
          property="og:description"
          content="Avvoka connects Kenya’s farmers to the world, exporting premium fresh produce, tea, and coffee. Discover our values and vision."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://avvoka.co.ke/about" />
        <meta
          property="og:image"
          content="https://avvoka.co.ke/assets/images/about-og-image.jpg"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@AvvokaExports" />
        <meta
          name="theme-color"
          content="#4CAF50"
          media="(prefers-color-scheme: light)"
        />
        <meta
          name="theme-color"
          content="#2E7D32"
          media="(prefers-color-scheme: dark)"
        />
      </Helmet>

      <Header />
      <div className="about-page">
        {/* Hero Section */}
        <section className="about-hero">
          <div className="hero-background">
            <div className="parallax-img img-1"></div>
            <div className="overlay"></div>
          </div>
          <div className="container">
            <div className="hero-content">
              <h1 className="hero-title ">
                Our <span className="text-accent">Story</span>
              </h1>
              <p className="hero-subtitle">
                From Kenyan farms to global tables since 2020
              </p>
            </div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="mission-section">
          <div className="container">
            <div className="mission-grid">
              <div className="mission-content">
                <h2 className="animate-on-scroll">Our Mission</h2>
                <p className="animate-on-scroll delay-1">
                  To connect the world with the finest Kenyan agricultural
                  products through sustainable export practices while supporting
                  local farming communities and maintaining the highest quality
                  standards.
                </p>
                <div className="mission-stats animate-on-scroll delay-2">
                  <div className="stat">
                    <span className="stat-number">50+</span>
                    <span className="stat-label">Countries Served</span>
                  </div>
                  <div className="stat">
                    <span className="stat-number">1000+</span>
                    <span className="stat-label">Farm Partners</span>
                  </div>
                  <div className="stat">
                    <span className="stat-number">99%</span>
                    <span className="stat-label">Quality Satisfaction</span>
                  </div>
                </div>
              </div>
              <div className="mission-image animate-right">
                <div className="image-frame">
                  <img
                    src={missionImage}
                    alt="Avvoka mission - Kenyan farm produce"
                    className="mission-img"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="values-section">
          <div className="parallax-img img-2"></div>
          <div className="container">
            <div className="section-header">
              <h2 className="animate-on-scroll">Our Values</h2>
              <p className="animate-on-scroll delay-1">
                The principles that guide everything we do
              </p>
            </div>
            <div className="values-grid">
              <div className="value-card animate-on-scroll">
                <div className="value-icon">
                  <div className="icon-circle">
                    <span>✓</span>
                  </div>
                </div>
                <h3>Quality Excellence</h3>
                <p>
                  Rigorous quality control ensures only the best products reach
                  our international clients.
                </p>
              </div>
              <div className="value-card animate-on-scroll delay-1">
                <div className="value-icon">
                  <div className="icon-circle">
                    <span>🌱</span>
                  </div>
                </div>
                <h3>Sustainability</h3>
                <p>
                  Environmentally responsible farming and export practices that
                  protect our planet.
                </p>
              </div>
              <div className="value-card animate-on-scroll delay-2">
                <div className="value-icon">
                  <div className="icon-circle">
                    <span>🤝</span>
                  </div>
                </div>
                <h3>Community Focus</h3>
                <p>
                  Supporting local farmers and communities through fair trade
                  practices.
                </p>
              </div>
              <div className="value-card animate-on-scroll delay-3">
                <div className="value-icon">
                  <div className="icon-circle">
                    <span>🌍</span>
                  </div>
                </div>
                <h3>Global Reach</h3>
                <p>
                  Building bridges between Kenyan producers and worldwide
                  markets.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Timeline Section */}
        <section className="timeline-section">
          <div className="container">
            <div className="section-header">
              <h2>Our Journey</h2>
              <p>From humble beginnings to international recognition</p>
            </div>
            <div className="timeline">
              <div className="timeline-item">
                <div className="timeline-year animate-on-scroll delay-3">
                  2020
                </div>
                <div className="timeline-content animate-right">
                  <h3>Foundation</h3>
                  <p>
                    Avvoka was established with a vision to export premium
                    Kenyan products worldwide.
                  </p>
                </div>
              </div>
              <div className="timeline-item">
                <div className="timeline-year animate-on-scroll delay-1">
                  2021
                </div>
                <div className="timeline-content animate-on-scroll delay-3">
                  <h3>First Export</h3>
                  <p>
                    Successfully shipped our first container of tea and coffee
                    to European markets.
                  </p>
                </div>
              </div>
              <div className="timeline-item">
                <div className="timeline-year animate-on-scroll delay-1">
                  2022
                </div>
                <div className="timeline-content animate-right">
                  <h3>Expansion</h3>
                  <p>
                    Expanded our product range to include fresh produce and
                    formed 500+ farmer partnerships.
                  </p>
                </div>
              </div>
              <div className="timeline-item">
                <div className="timeline-year animate-on-scroll delay-1">
                  2023
                </div>
                <div className="timeline-content animate-on-scroll delay-1">
                  <h3>Global Reach</h3>
                  <p>
                    Reached 50+ countries and received international quality
                    certifications.
                  </p>
                </div>
              </div>
              <div className="timeline-item">
                <div className="timeline-year animate-on-scroll delay-1">
                  2024
                </div>
                <div className="timeline-content animate-on-scroll delay-4">
                  <h3>Innovation</h3>
                  <p>
                    Implemented sustainable packaging and advanced tracking
                    systems for all exports.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* CTA Section */}
        <section className="about-cta">
          <div className="container">
            <div className="cta-content ">
              <h2 className="animate-right">
                Ready to Experience Kenyan Quality?
              </h2>
              <p className="animate-left">
                Join our global family of satisfied clients and taste the
                difference that quality makes.
              </p>
              <div className="cta-buttons animate-on-scroll delay-1">
                <button className="btn btn-primary">Request a Quote</button>
                <button className="btn btn-secondary">View Products</button>
              </div>
            </div>
          </div>
        </section>
        <Footer />

        {/* Add animation classes to other sections similarly */}
        {/* ... rest of the component ... */}
      </div>
    </>
  );
};

export default About;
